import { TableV2 as Table, ColumnType, Row } from "@asayinc/component-library";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { ErrorState } from "src/components/atoms/ErrorState";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { LoadingCard } from "src/components/molecules/Loading";
import {
  ADDITIONAL_MAILING_TYPE_LABEL,
  ADDITIONAL_MAILING_AUDIENCE_LABEL,
} from "src/constants/labels/additionalMailingLabel";
import { useProxyEventAdditionalMailingsQuery } from "src/store/proxyEvent";
import { IProxyEventAdditionalMailingForGraph } from "src/store/proxyEvent/types";
import {
  AdditionalMailingType,
  AdditionalMailingAudience,
} from "src/store/additionalMailing/types";

const COLUMNS = [
  "type",
  "audience",
  "dateReceived",
  "operationsStatus",
  "approvedAt",
];

type ColumnData = {
  type: ColumnType;
  audience: ColumnType;
  dateReceived: ColumnType;
  operationsStatus: ColumnType;
  approvedAt: ColumnType;
};

function formatOperationsStatus(status: string): string {
  if (!status) return "-";
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getColumnData(): ColumnData {
  return {
    type: {
      style: "text",
      title: "Type",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
    },
    audience: {
      style: "text",
      title: "Audience",
    },
    dateReceived: {
      style: "text",
      title: "Date Received",
    },
    operationsStatus: {
      style: "text",
      title: "Status",
    },
    approvedAt: {
      style: "text",
      title: "Approved At",
    },
  };
}

function getRowData(
  mailings: IProxyEventAdditionalMailingForGraph[],
  onRowClick: (mailing: IProxyEventAdditionalMailingForGraph) => void
): Row[] {
  const handleRowClick = (data: unknown) => {
    const { mailing } = data as {
      mailing: IProxyEventAdditionalMailingForGraph;
      idx: string;
    };
    onRowClick(mailing);
  };

  return mailings.map((mailing, idx) => {
    const { id, type, audience, dateReceived, operationsStatus, approvedAt } =
      mailing;

    return {
      id,
      data: { mailing, idx },
      rowData: {
        type: {
          content:
            ADDITIONAL_MAILING_TYPE_LABEL[type as AdditionalMailingType] ||
            type,
          clickHandler: handleRowClick,
        },
        audience: {
          content:
            ADDITIONAL_MAILING_AUDIENCE_LABEL[
              audience as AdditionalMailingAudience
            ] ||
            audience ||
            "-",
          clickHandler: handleRowClick,
        },
        dateReceived: {
          content: dateReceived
            ? dayjs(dateReceived).format("MM/DD/YYYY")
            : "-",
          clickHandler: handleRowClick,
        },
        operationsStatus: {
          content: formatOperationsStatus(operationsStatus),
          clickHandler: handleRowClick,
        },
        approvedAt: {
          content: approvedAt
            ? dayjs(approvedAt).format("MM/DD/YYYY HH:mm")
            : "-",
          clickHandler: handleRowClick,
        },
      },
    };
  });
}

export function ProxyEventAdditionalMailingsTab() {
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const {
    data: additionalMailings,
    isLoading,
    error,
  } = useProxyEventAdditionalMailingsQuery({ id });

  const handleRowClick = (mailing: IProxyEventAdditionalMailingForGraph) => {
    navigate(`/additional-mailings/${mailing.id}/details`);
  };

  const columns = useMemo(() => COLUMNS, []);
  const columnData = useMemo(() => getColumnData(), []);
  const rowData = useMemo(
    () => getRowData(additionalMailings || [], handleRowClick),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [additionalMailings]
  );

  if (error) {
    return <ErrorState />;
  }

  if (isLoading) {
    return (
      <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
        <LoadingCard />
      </ScrollContainer>
    );
  }

  return (
    <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
      <Box>
        <Table
          count={additionalMailings?.length || 0}
          columnData={columnData}
          columns={columns}
          isFetching={false}
          isLoading={isLoading}
          noResultsData={{
            title: "No Additional Mailings",
            subtitle: "There are no additional mailings for this proxy event.",
          }}
          rows={rowData}
          tableMinWidth={700}
          title="Additional mailings"
          titleVariant="subtitle2"
        />
      </Box>
    </ScrollContainer>
  );
}
